import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faCheck, faMinus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { forwardRef } from "react";
import styled, { css } from "styled-components";
import { colorPalette } from "../../theme/styled-theme";
import { cssBody400 } from "../typography/styles";
const _NewCheckbox = forwardRef(({ indeterminate, categoryColor = "blue600", $focus, $active, $hover, children, className, ...rest }, ref) => {
    return (_jsxs(Container, { className: className, children: [_jsx(Input, { "$categoryColor": categoryColor, "$focus": $focus, "$hover": $hover, "$active": $active, type: "checkbox", ...rest, ref: ref }), _jsx(Box, { children: !indeterminate ? _jsx(Icon, { icon: faCheck }) : _jsx(Icon, { icon: faMinus }) }), children && _jsx(Text, { children: children })] }));
});
const Icon = styled(FontAwesomeIcon) `
  color: ${(p) => p.theme.systemColors.white};
  position: relative;
  font-size: 16px;
`;
const Container = styled.label `
  cursor: pointer;
  display: flex;
  position: relative;
  user-select: none;
`;
const Box = styled.span `
  margin-top: 2px;
  transition: all 0.2s ease-in-out;
  position: relative;
  min-width: 20px;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid ${(p) => p.theme.systemColors.grey400};
  z-index: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  & > svg {
    display: none;
  }

  &:after {
    position: absolute;
    left: -5px;
    right: -5px;
    top: -5px;
    bottom: -5px;
    border-radius: 7px;
    border: 2px solid ${(p) => p.theme.systemColors.blue600};
    content: "";
    display: none;
    z-index: -1;
  }

  ${(p) => p.$active &&
    css `
      background-color: ${(p) => p.theme.systemColors.blue600};
      border-color: ${(p) => p.theme.systemColors.blue600};

      & > svg {
        display: block;
      }
    `}

  ${(p) => p.$disabled &&
    css `
      background-color: ${(p) => p.theme.systemColors.grey200};
      border-color: ${(p) => p.theme.systemColors.grey200};
    `}
`;
const Input = styled.input `
  width: 0;
  height: 0;
  opacity: 0;
  margin: 0;

  //state:keyboard-focus
  &:focus-visible + ${Box} {
    &:after {
      display: block;
    }
  }

  //state:hover
  &:hover + ${Box} {
    border-color: ${(p) => p.theme.systemColors.grey600};
  }

  //state:focus
  &:focus + ${Box} {
    border-color: ${(p) => p.theme.systemColors.grey600};
  }

  //state:disabled, checked
  &:checked[disabled] + ${Box} {
    background-color: ${(p) => p.theme.systemColors.grey200};
    border-color: ${(p) => p.theme.systemColors.grey200};
  }

  //state:disabled
  &[disabled] + ${Box} {
    background-color: ${(p) => p.theme.systemColors.grey200};
    border-color: ${(p) => p.theme.systemColors.grey200};
    cursor: not-allowed;
  }

  //state:checked
  &:checked + ${Box} {
    background-color: ${(p) => colorPalette[p.$categoryColor]};
    border-color: ${(p) => colorPalette[p.$categoryColor]};
  }

  //state:checked hover
  &:checked:not([disabled]):hover + ${Box} {
    ${(p) => getColor(p.$categoryColor, "hover")}
  }

  //state:checked active
  &:checked:not([disabled]):active + ${Box} {
    ${(p) => getColor(p.$categoryColor, "pressed")}
  }

  //state:checked
  &:checked + ${Box} > svg {
    display: block;
  }

  //state:keyboard-focus / $keyboardFocus
  ${({ $focus }) => $focus &&
    css `
      & + ${Box} {
        &:after {
          display: block;
        }
      }
    `} //state:$active
  ${({ $active, ...rest }) => $active &&
    css `
      &:checked:not([disabled]) + ${Box} {
        ${getColor(rest.$categoryColor, "pressed")}
      }
    `} //state:$hover
  ${({ $hover, ...rest }) => $hover &&
    css `
      & + ${Box} {
        border-color: ${() => rest.theme.systemColors.grey600};
      }
    `} //state:checked $hover
  ${({ $hover, ...rest }) => $hover &&
    css `
      &:checked:not([disabled]) + ${Box} {
        ${() => getColor(rest.$categoryColor, "hover")}
      }
    `}
`;
const Text = styled.span `
  ${cssBody400};
  color: ${(p) => p.theme.systemColors.grey600};
  margin-left: ${(p) => p.theme.spacing.sp8};

  & > svg {
    fill: ${(p) => p.theme.systemColors.grey600};
    margin-left: 3px;
    position: relative;
    bottom: 1px;
  }
`;
const getColor = (category, type) => {
    const textTone = category.replace(/^\D+/g, "");
    const color = category.replace(textTone, "");
    let tone = Number(textTone) + (type === "hover" ? 100 : 200);
    tone = tone > 1000 ? 1000 : tone;
    return css `
    background-color: ${colorPalette[`${color}${tone}`]};
    border-color: ${colorPalette[`${color}${tone}`]};
  `;
};
export const NewCheckbox = Object.assign(_NewCheckbox, {
    Container,
    Input,
    Box,
    Text,
    Icon,
});
